import { PREDEFINED_SECTIONS, PredefinedSectionSlug, usePredefinedSectionFilter } from './predefined-sections'
import { useFilterContext } from '../../context/filter-context'
import { usePaginatedWorkouts } from '../../hook/use-paginated-workouts'
import React, { useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { PageContainer } from 'src/component/page-container/page-container'
import { BackArrow } from 'src/component/programmes/programme-back-arrow'
import { WorkoutPopup } from 'src/component/programmes/programme-workout-popup'
import { SortByDropdown } from 'src/component/workout-filter/sort-by-dropdown'
import { PaginationControls } from 'src/component/ui/pagination-controls'
import { WorkoutCardList } from 'src/component/ui/workout-card/workout-card-list'
import { WorkoutCardSkeleton } from 'src/component/ui/workout-card/workout-card-skeleton'
import { useSessionContext } from 'src/context/session-context'
import { Workout } from 'src/dao/core-dao'

/**
 * Dedicated landing page for a predefined section (GH-754): every workout matching the section's
 * filter equivalence, with a Sort By control and 27/page pagination, and no filter panel. Sort and
 * page come from the URL via the filter context (the section's own filters come from its slug, not
 * the panel); the shared hook owns the query + scroll.
 */
export const SectionPage = ({ slug }: { slug: PredefinedSectionSlug }): React.ReactElement => {
  const section = PREDEFINED_SECTIONS.find((candidate) => candidate.slug === slug)
  const { filter: baseFilter, isLoading: isFilterLoading } = usePredefinedSectionFilter(slug)
  const { completedWorkoutIds } = useSessionContext()
  const { sort, setSort, page, setPage } = useFilterContext()
  const [selectedWorkout, setSelectedWorkout] = useState<Workout | undefined>(undefined)

  const { workouts, totalPages, isLoading: areResultsLoading } = usePaginatedWorkouts(baseFilter, sort, { page, setPage })
  // Still loading while the filter is resolving, or while the (enabled) results query runs. When the
  // filter settles to undefined (e.g. durations failed) we fall through to the empty state rather
  // than hanging on skeletons, since the results query stays disabled.
  const isLoading = isFilterLoading || (!!baseFilter && areResultsLoading)
  const isEmpty = !isLoading && workouts.length === 0

  return (
    <PageContainer>
      <div className="mt-5 pb-5">
        <Row className="mb-6">
          <BackArrow />
        </Row>
        <div className="d-flex justify-content-between align-items-end mb-5 flex-wrap gap-2">
          <h1 className="font-evogria font-normal text-[57px] leading-[64px] tracking-[-0.25px] text-primary uppercase">
            {section?.label}
          </h1>
          <SortByDropdown value={sort} onChange={setSort} />
        </div>

        <Row>
          {isLoading &&
            Array.from({ length: 9 }).map((_, index) => (
              <Col key={index} xs={12} sm={6} lg={4} className="mb-6">
                <WorkoutCardSkeleton />
              </Col>
            ))}
          {!isLoading &&
            workouts.map((workout) => (
              <Col key={workout.id} xs={12} sm={6} lg={4} className="mb-6">
                <WorkoutCardList
                  workout={workout}
                  isCompleted={completedWorkoutIds.has(workout.id)}
                  onAction={() => setSelectedWorkout(workout)}
                />
              </Col>
            ))}
          {isEmpty && <p className="font-roboto-condensed text-ivory">No workouts found.</p>}
        </Row>

        {!isEmpty && <PaginationControls page={page} totalPages={totalPages} onPageChange={setPage} className="mt-4" />}
      </div>

      <WorkoutPopup
        setOnClose={() => setSelectedWorkout(undefined)}
        selectedWorkout={selectedWorkout}
        startWorkoutLink={selectedWorkout ? `/workouts/${selectedWorkout.id}` : undefined}
      />
    </PageContainer>
  )
}
